<template>
  <div class="animated fadeIn agents-page">

    <!-- HEADER -->
    <div class="pk-page-header">
      <div class="pk-page-header__left">
        <div class="pk-page-header__icon pk-page-header__icon--purple">
          <i class="fas fa-robot"></i>
        </div>
        <div>
          <h2 class="pk-page-header__title">{{ $t('kortex.agents') }}</h2>
          <p class="pk-page-header__sub" v-if="agents.length > 0">{{ agents.length }} total</p>
        </div>
      </div>
      <button v-if="canCreateAgents || loading" class="pk-btn-primary" @click="openCreateDialog">
        <i class="fas fa-plus"></i> {{ $t('kortex.createAgent') }}
      </button>
    </div>

    <!-- Upgrade gate -->
    <div v-if="!loading && !canCreateAgents" class="pk-upgrade-gate">
      <div class="pk-upgrade-gate__icon"><i class="fas fa-lock"></i></div>
      <h3 class="pk-upgrade-gate__title">{{ $t('kortex.upgradeToUseAgents') }}</h3>
      <p class="pk-upgrade-gate__desc">{{ $t('kortex.upgradeToUseAgentsDesc') }}</p>
      <router-link :to="{ name: 'marketplace.List', query: { open: 'protokol-kortex' } }" class="pk-upgrade-gate__btn">
        <i class="fas fa-arrow-up"></i> {{ $t('kortex.upgradePlan') }}
      </router-link>
    </div>

    <!-- CARD -->
    <div v-if="loading || canCreateAgents" class="pk-card">
      <pk-data-table
        :columns="columns"
        :rows="agents"
        :loading="loading"
        row-key="uuid"
        empty-icon="fas fa-robot"
        :empty-text="$t('kortex.noAgents')"
      >
        <template #cell(name)="{ row }">
          <strong class="pk-name">{{ row.name }}</strong>
          <div v-if="row.description" class="pk-sub-text">{{ row.description }}</div>
        </template>

        <template #cell(knowledge_bases)="{ row }">
          <span v-if="row.knowledge_bases && row.knowledge_bases.length">
            <span
              v-for="kbUUID in row.knowledge_bases"
              :key="kbUUID"
              class="pk-kb-badge"
            >{{ kbNameByUUID(kbUUID) }}</span>
          </span>
          <span v-else class="pk-date">—</span>
        </template>

        <template #cell(created_at)="{ row }">
          <span class="pk-date">{{ formatDate(row.created_at) }}</span>
        </template>

        <template #cell(actions)="{ row }">
          <button
            v-if="!isDefaultAgent(row)"
            class="action-btn"
            @click.stop="toggleDropdown($event, row)"
          >
            <i class="fas fa-ellipsis-v"></i>
          </button>
        </template>
      </pk-data-table>
    </div>

    <!-- Fixed action dropdown -->
    <div
      v-if="dropdownRow"
      class="agents-fixed-dropdown"
      :style="dropdownStyle"
      @click.stop
    >
      <button class="action-dropdown__item" @click="openEditDialog(dropdownRow); closeDropdown()">
        <i class="fas fa-pencil-alt"></i> {{ $t('edit') }}
      </button>
      <div class="action-dropdown__divider"></div>
      <button class="action-dropdown__item action-dropdown__item--danger" @click="confirmDelete(dropdownRow); closeDropdown()">
        <i class="fas fa-trash"></i> {{ $t('delete') }}
      </button>
    </div>

    <!-- Create / Edit Agent dialog -->
    <main-dialog
      :show="showFormDialog"
      :title="editingAgent ? $t('kortex.editAgent') : $t('kortex.createAgent')"
      @close="closeFormDialog"
    >
      <b-form @submit.prevent="save">
        <b-form-group :label="$t('kortex.name')" label-for="agent-name">
          <b-form-input
            id="agent-name"
            v-model="form.name"
            :placeholder="$t('kortex.agentNamePlaceholder')"
            required
          ></b-form-input>
        </b-form-group>
        <b-form-group :label="$t('kortex.description')" label-for="agent-desc">
          <b-form-input
            id="agent-desc"
            v-model="form.description"
            :placeholder="$t('kortex.descriptionPlaceholder')"
          ></b-form-input>
        </b-form-group>
        <b-form-group :label="$t('kortex.systemPrompt')" label-for="agent-prompt">
          <b-form-textarea
            id="agent-prompt"
            v-model="form.system_prompt"
            :placeholder="$t('kortex.systemPromptPlaceholder')"
            rows="5"
          ></b-form-textarea>
        </b-form-group>
        <!-- LLM Generation Settings -->
        <div class="llm-settings-section">
          <div class="llm-settings-header">
            <i class="fas fa-sliders-h"></i>
            {{ $t('kortex.llmSettings') }}
          </div>
          <b-row>
            <b-col sm="6">
              <b-form-group :label="$t('kortex.temperature')" label-for="agent-temp">
                <b-form-input
                  id="agent-temp"
                  v-model.number="form.llm_settings.temperature"
                  type="number"
                  min="0"
                  max="2"
                  step="0.1"
                  :placeholder="$t('kortex.temperaturePlaceholder')"
                ></b-form-input>
                <small class="form-text text-muted">0 – 2, {{ $t('kortex.temperatureHint') }}</small>
              </b-form-group>
            </b-col>
            <b-col sm="6">
              <b-form-group :label="$t('kortex.topP')" label-for="agent-topp">
                <b-form-input
                  id="agent-topp"
                  v-model.number="form.llm_settings.top_p"
                  type="number"
                  min="0"
                  max="1"
                  step="0.05"
                  :placeholder="$t('kortex.topPPlaceholder')"
                ></b-form-input>
                <small class="form-text text-muted">0 – 1, {{ $t('kortex.topPHint') }}</small>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col sm="6">
              <b-form-group :label="$t('kortex.maxTokens')" label-for="agent-maxtokens">
                <b-form-input
                  id="agent-maxtokens"
                  v-model.number="form.llm_settings.max_tokens"
                  type="number"
                  min="0"
                  step="1"
                  :placeholder="$t('kortex.maxTokensPlaceholder')"
                ></b-form-input>
                <small class="form-text text-muted">{{ $t('kortex.maxTokensHint') }}</small>
              </b-form-group>
            </b-col>
          </b-row>
        </div>
        <!-- Model Tier Selection -->
        <div class="llm-settings-section mt-3">
          <div class="llm-settings-header">
            <i class="fas fa-layer-group"></i>
            {{ $t('kortex.modelTier') }}
          </div>
          <div class="model-tier-pills">
            <div
              v-for="option in modelTierOptions"
              :key="option.value"
              class="model-tier-pill"
              :class="[
                'model-tier-pill--' + option.value,
                { 'model-tier-pill--selected': form.model_tier === option.value }
              ]"
              @click="form.model_tier = option.value"
            >
              <i :class="option.icon"></i>
              <div class="model-tier-pill__body">
                <span class="model-tier-pill__label">{{ option.label }}</span>
                <span class="model-tier-pill__hint">{{ option.hint }}</span>
              </div>
              <span class="model-tier-pill__check"><i class="fas fa-check"></i></span>
            </div>
          </div>
        </div>

        <!-- Knowledge Base Selection -->
        <b-form-group :label="$t('kortex.selectKnowledgeBases')" class="mt-3">
          <div v-if="knowledgeBases.length === 0" class="kb-empty-hint">
            <i class="fas fa-database"></i> {{ $t('kortex.noKnowledgeBases') }}
          </div>
          <div v-else class="kb-pill-grid kb-pill-grid--inline">
            <div
              v-for="kb in knowledgeBases"
              :key="kb.uuid"
              class="kb-pill"
              :class="{ 'kb-pill--selected': form.knowledge_bases.indexOf(kb.uuid) !== -1 }"
              @click="toggleKb(kb.uuid)"
            >
              <span class="kb-pill__icon"><i class="fas fa-database"></i></span>
              <span class="kb-pill__name">{{ kb.name }}</span>
              <span v-if="integratedKbMeta(kb.uuid)" class="kb-pill__meta">
                {{ integratedKbMeta(kb.uuid).documents }} {{ $t('kortex.documents').toLowerCase() }}
              </span>
              <span class="kb-pill__check"><i class="fas fa-check"></i></span>
            </div>
          </div>
          <small v-if="integratedKBsLoading" class="form-text text-muted mt-2">
            <b-spinner small></b-spinner> {{ $t('kortex.loadingIntegratedKBs') }}
          </small>
        </b-form-group>
      </b-form>
      <template #footer>
        <b-button variant="secondary" @click="closeFormDialog">
          {{ $t('cancel') }}
        </b-button>
        <b-button variant="primary" :disabled="saving" @click="save">
          <b-spinner v-if="saving" small class="mr-1"></b-spinner>
          {{ $t('save') }}
        </b-button>
      </template>
    </main-dialog>

    <!-- Delete confirmation dialog -->
    <main-dialog
      :show="showDeleteDialog"
      :title="$t('kortex.deleteAgent')"
      @close="showDeleteDialog = false"
    >
      <p>{{ $t('kortex.confirmDeleteAgent') }}</p>
      <template #footer>
        <b-button variant="secondary" @click="showDeleteDialog = false">
          {{ $t('cancel') }}
        </b-button>
        <b-button variant="danger" :disabled="deleting" @click="doDelete">
          <b-spinner v-if="deleting" small class="mr-1"></b-spinner>
          {{ $t('delete') }}
        </b-button>
      </template>
    </main-dialog>

  </div>
</template>

<script>
export default {
  name: 'Agents',
  data: function () {
    return {
      loading: false,
      saving: false,
      deleting: false,
      showFormDialog: false,
      showDeleteDialog: false,
      editingAgent: null,
      pendingDelete: null,
      activeActionRow: null,
      dropdownRow: null,
      dropdownPos: { top: 0, right: 0 },
      form: {
        name: '',
        description: '',
        system_prompt: '',
        knowledge_bases: [],
        model_tier: '',
        llm_settings: {
          temperature: null,
          top_p: null,
          max_tokens: null
        }
      },
      integratedKBs: [],
      integratedKBsLoading: false
    }
  },
  computed: {
    canCreateAgents: function () {
      return this.$store.getters['cortex/canCreateAgents']
    },
    projectTier: function () {
      return this.$store.state.cortex.tier || 'free'
    },
    modelTierOptions: function () {
      var self = this
      var tierRank = { free: 0, standard: 1, pro: 2 }
      var projectRank = tierRank[self.projectTier] !== undefined ? tierRank[self.projectTier] : 0
      var options = [
        { value: 'free', label: self.$t('kortex.modelTierFree'), hint: self.$t('kortex.modelTierFreeHint'), icon: 'fas fa-leaf' },
        { value: 'standard', label: self.$t('kortex.modelTierStandard'), hint: self.$t('kortex.modelTierStandardHint'), icon: 'fas fa-bolt' },
        { value: 'pro', label: self.$t('kortex.modelTierPro'), hint: self.$t('kortex.modelTierProHint'), icon: 'fas fa-gem' }
      ]
      return options.filter(function (o) { return tierRank[o.value] <= projectRank })
    },
    agents: function () {
      return this.$store.state.cortex.agents
    },
    knowledgeBases: function () {
      return this.$store.state.cortex.knowledgeBases
    },
    kbOptions: function () {
      return this.knowledgeBases.map(function (kb) {
        return { value: kb.uuid, text: kb.name }
      })
    },
    dropdownStyle: function () {
      return {
        position: 'absolute',
        top: this.dropdownPos.top + 'px',
        right: this.dropdownPos.right + 'px',
        zIndex: 9999
      }
    },
    columns: function () {
      return [
        { key: 'name', label: this.$t('kortex.name') },
        { key: 'knowledge_bases', label: this.$t('kortex.knowledgeBases') },
        { key: 'created_at', label: this.$t('createdAt') },
        { key: 'actions', label: '', tdClass: 'pk-td-actions' }
      ]
    }
  },
  mounted: function () {
    this.load()
    document.addEventListener('click', this.closeDropdown)
    window.addEventListener('scroll', this.closeDropdown, true)
    window.addEventListener('resize', this.closeDropdown)
  },
  beforeDestroy: function () {
    document.removeEventListener('click', this.closeDropdown)
    window.removeEventListener('scroll', this.closeDropdown, true)
    window.removeEventListener('resize', this.closeDropdown)
  },
  methods: {
    load: function () {
      var self = this
      self.loading = true
      Promise.all([
        self.$store.dispatch('cortex/loadTier'),
        self.$store.dispatch('cortex/loadAgents'),
        self.$store.dispatch('cortex/loadKnowledgeBases')
      ]).finally(function () {
        self.loading = false
      })
    },
    closeDropdown: function () {
      this.activeActionRow = null
      this.dropdownRow = null
    },
    toggleDropdown: function (event, row) {
      if (this.dropdownRow && this.dropdownRow.uuid === row.uuid) {
        this.dropdownRow = null
        return
      }
      var btn = event.currentTarget
      var rect = btn.getBoundingClientRect()
      var containerRect = this.$el.getBoundingClientRect()
      this.dropdownPos = {
        top: rect.bottom - containerRect.top + 4,
        right: containerRect.right - rect.right
      }
      this.dropdownRow = row
      this.activeActionRow = row.uuid
    },
    openCreateDialog: function () {
      this.editingAgent = null
      this.form = { name: '', description: '', system_prompt: '', knowledge_bases: [], model_tier: '', llm_settings: { temperature: null, top_p: null, max_tokens: null } }
      this.showFormDialog = true
    },
    openEditDialog: function (agent) {
      this.editingAgent = agent
      var kbUUIDs = agent.knowledge_bases && agent.knowledge_bases.length ? agent.knowledge_bases.slice() : []
      var llmSettings = agent.llm_settings || {}
      this.form = {
        name: agent.name || '',
        description: agent.description || '',
        system_prompt: agent.system_prompt || '',
        knowledge_bases: kbUUIDs,
        model_tier: agent.model_tier || '',
        llm_settings: {
          temperature: llmSettings.temperature != null ? llmSettings.temperature : null,
          top_p: llmSettings.top_p != null ? llmSettings.top_p : null,
          max_tokens: llmSettings.max_tokens != null ? llmSettings.max_tokens : null
        }
      }
      this.showFormDialog = true
      // Load full KB objects for the KBs already integrated with this agent
      var self = this
      self.integratedKBs = []
      self.integratedKBsLoading = true
      this.$store.dispatch('cortex/loadIntegratedKBs', agent.uuid).then(function (kbs) {
        self.integratedKBs = kbs || []
      }).finally(function () {
        self.integratedKBsLoading = false
      })
    },
    save: function () {
      var self = this
      if (!self.form.name.trim()) return
      self.saving = true
      var action = self.editingAgent
        ? self.$store.dispatch('cortex/updateAgent', Object.assign({ uuid: self.editingAgent.uuid }, self.form))
        : self.$store.dispatch('cortex/createAgent', self.form)
      action.then(function () {
        self.closeFormDialog()
      }).finally(function () {
        self.saving = false
      })
    },
    confirmDelete: function (agent) {
      this.pendingDelete = agent
      this.showDeleteDialog = true
    },
    doDelete: function () {
      var self = this
      if (!self.pendingDelete) return
      self.deleting = true
      self.$store.dispatch('cortex/deleteAgent', self.pendingDelete.uuid).then(function () {
        self.showDeleteDialog = false
        self.pendingDelete = null
      }).finally(function () {
        self.deleting = false
      })
    },
    closeFormDialog: function () {
      this.showFormDialog = false
      this.editingAgent = null
      this.integratedKBs = []
      this.integratedKBsLoading = false
      this.form = { name: '', description: '', system_prompt: '', knowledge_bases: [], model_tier: '', llm_settings: { temperature: null, top_p: null, max_tokens: null } }
    },
    integratedKbMeta: function (uuid) {
      var found = this.integratedKBs.find(function (kb) { return kb.uuid === uuid })
      if (!found) return null
      return { documents: found.document_count || 0, chunks: found.chunk_count || 0, status: found.status }
    },
    toggleKb: function (uuid) {
      var idx = this.form.knowledge_bases.indexOf(uuid)
      if (idx === -1) {
        this.form.knowledge_bases.push(uuid)
      } else {
        this.form.knowledge_bases.splice(idx, 1)
      }
    },
    kbNameByUUID: function (uuid) {
      var kb = this.knowledgeBases.find(function (k) { return k.uuid === uuid })
      return kb ? kb.name : uuid
    },
    isDefaultAgent: function (agent) {
      return agent.uuid === 'prota' || agent.uuid === 'proka' || agent.uuid === 'protokol-bot'
    },
    formatDate: function (dateStr) {
      if (!dateStr) return ''
      return new Date(dateStr).toLocaleDateString()
    }
  }
}
</script>

<style scoped>
.agents-page {
  padding: 8px 0;
  position: relative;
}
.agents-fixed-dropdown {
  background: #1e2433;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 10px;
  padding: 6px;
  min-width: 160px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.45);
}
.llm-settings-section {
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 8px;
  padding: 16px;
  margin-top: 8px;
  background: rgba(255,255,255,0.02);
}
.llm-settings-header {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.45);
  margin-bottom: 14px;
}
.llm-settings-header i {
  margin-right: 6px;
}
.llm-settings-subsection-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.35);
  margin-bottom: 8px;
}
.llm-settings-divider {
  border: none;
  border-top: 1px solid rgba(255,255,255,0.07);
  margin: 16px 0;
}

/* Knowledge base pill selector */
.kb-pill-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
}
.kb-pill {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 7px 14px 7px 10px;
  border-radius: 8px;
  border: 1px solid rgba(255,255,255,0.1);
  background: rgba(255,255,255,0.03);
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  user-select: none;
}
.kb-pill:hover {
  border-color: rgba(108,93,211,0.5);
  background: rgba(108,93,211,0.08);
}
.kb-pill--selected {
  border-color: #6c5dd3;
  background: rgba(108,93,211,0.18);
}
.kb-pill__icon {
  font-size: 12px;
  color: rgba(255,255,255,0.4);
}
.kb-pill--selected .kb-pill__icon {
  color: #a89af5;
}
.kb-pill__name {
  font-size: 13px;
  font-weight: 500;
  color: rgba(255,255,255,0.75);
}
.kb-pill--selected .kb-pill__name {
  color: #fff;
}
.kb-pill__meta {
  font-size: 10px;
  color: rgba(255,255,255,0.3);
  margin-left: auto;
}
.kb-pill--selected .kb-pill__meta {
  color: rgba(255,255,255,0.45);
}
.kb-pill__check {
  font-size: 11px;
  color: #6c5dd3;
  opacity: 0;
  transition: opacity 0.15s;
}
.kb-pill--selected .kb-pill__check {
  opacity: 1;
}
.kb-empty-hint {
  font-size: 13px;
  color: rgba(255,255,255,0.35);
  padding: 8px 0;
}
.kb-empty-hint i {
  margin-right: 5px;
}

/* Model tier selector */
.model-tier-pills {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.model-tier-pill {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-radius: 8px;
  border: 1px solid rgba(255,255,255,0.1);
  background: rgba(255,255,255,0.03);
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}
.model-tier-pill > i {
  font-size: 14px;
  width: 18px;
  text-align: center;
  color: rgba(255,255,255,0.4);
}
.model-tier-pill__body {
  display: flex;
  flex-direction: column;
  flex: 1;
}
.model-tier-pill__label {
  font-size: 13px;
  font-weight: 600;
  color: rgba(255,255,255,0.8);
}
.model-tier-pill__hint {
  font-size: 11px;
  color: rgba(255,255,255,0.35);
  margin-top: 2px;
}
.model-tier-pill__check {
  font-size: 11px;
  color: #6c5dd3;
  opacity: 0;
  transition: opacity 0.15s;
}
.model-tier-pill--selected .model-tier-pill__check {
  opacity: 1;
}
.model-tier-pill--free:hover,
.model-tier-pill--free.model-tier-pill--selected {
  border-color: #27ae60;
  background: rgba(39,174,96,0.1);
}
.model-tier-pill--free > i { color: #27ae60; }
.model-tier-pill--standard:hover,
.model-tier-pill--standard.model-tier-pill--selected {
  border-color: #2980b9;
  background: rgba(41,128,185,0.1);
}
.model-tier-pill--standard > i { color: #2980b9; }
.model-tier-pill--pro:hover,
.model-tier-pill--pro.model-tier-pill--selected {
  border-color: #8e44ad;
  background: rgba(142,68,173,0.1);
}
.model-tier-pill--pro > i { color: #a89af5; }

/* Upgrade gate */
.pk-upgrade-gate {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  text-align: center;
}
.pk-upgrade-gate__icon {
  font-size: 48px;
  color: rgba(255,255,255,0.15);
  margin-bottom: 20px;
}
.pk-upgrade-gate__title {
  font-size: 20px;
  font-weight: 600;
  color: rgba(255,255,255,0.85);
  margin-bottom: 10px;
}
.pk-upgrade-gate__desc {
  font-size: 14px;
  color: rgba(255,255,255,0.45);
  max-width: 420px;
  line-height: 1.6;
  margin-bottom: 28px;
}
.pk-upgrade-gate__btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 24px;
  border-radius: 8px;
  background: #6c5dd3;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.15s;
}
.pk-upgrade-gate__btn:hover {
  background: #7d6fe0;
  color: #fff;
  text-decoration: none;
}
</style>
